import { redirect, render } from 'vike/abort';

import { request } from '@/renderer/utils/api';
import { ExpiredSessionError, RequestError } from '@/renderer/utils/errors';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { authToken } from '@/renderer/utils/useMe';
import type { RequestSchema } from '@/types/request';

export interface GuardOptions {
  /** Before fetching data, return a value to `throw redirect` */
  redirectToBefore?: () => Parameters<typeof redirect>;
  /** Before fetching data, return a value to `throw render` */
  renderToBefore?: () => Parameters<typeof render>;
  /** After fetching data, return a value to `throw redirect` */
  redirectTo?: (data: unknown) => Parameters<typeof redirect>;
  /** After fetching data, return a value to `throw render` */
  renderTo?: (data: unknown) => Parameters<typeof render>;
  /** If 'server' (default), only fetch user on SSR. 'client' on every CSR page view, including when taking over SSR */
  fetchOn?: 'server' | 'client' | 'both';
  /** If false (default), will immediately `throw render(/signin)` if user is logged out (null) */
  ignoreLoggedOut?: boolean;
}

type Expired = { expired: true };

function isExpired<T>(data: T | { expired: true }): data is Expired {
  return (data as Expired)?.expired === true;
}

/**
 * In SSR, fetches initial user data. As `guard` runs before `data`, this actually does both (if necessary).
 * `request` automatically handles token refreshes.
 * In CSR, this handles guarding of routes. Using it as +data.shared.ts is isometric, preventing pageContext.json calls.
 */
export async function dataguard(pageContext: Vike.PageContext, guardOptions?: GuardOptions) {
  // Check if guard needs to redirect before calling for data

  if (guardOptions?.redirectToBefore) {
    const to = guardOptions.redirectToBefore();

    if (to) {
      throw redirect(...to);
    }
  }

  // Check if guard needs to render before calling for data

  if (guardOptions?.renderToBefore) {
    const to = guardOptions.renderToBefore();

    if (to) {
      throw render(...to);
    }
  }

  // const fetchOn = guardOptions?.fetchOn || 'server';
  const fetchOn = 'both'; // TEMPORARY FIX

  let user: API.User | null | undefined = undefined;
  let userPromise: Promise<API.User | null | undefined> | undefined = undefined;

  if (
    (fetchOn === 'server' && IS_SERVER) ||
    (fetchOn === 'client' && !IS_SERVER) ||
    fetchOn === 'both'
  ) {
    const cookies =
      'headers' in pageContext &&
      pageContext.headers?.cookie?.split &&
      Object.fromEntries(
        pageContext.headers.cookie
          ?.split?.('; ')
          .map((v) => v.split(/=(.*)/s).map(decodeURIComponent)),
      );

    const authTokenValue = authToken();

    // pageContext.fetch is always set in prod; the backup should pretty much only ever occur on the vite:dev server
    const devFetch = (url: string, init?: RequestInit<RequestInitCfProperties>) =>
      fetch(url, {
        // Cloudflare Workers don't support fetch.credentials
        credentials: IS_SERVER ? undefined : 'include',
        // Cloudflare Workers don't support fetch.mode
        mode: IS_SERVER ? undefined : 'cors',
        ...init,
        headers: {
          ...(('headers' in pageContext && pageContext.headers) || undefined),
          ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'function'
            ? Object.fromEntries(init.headers.entries() as IterableIterator<[string, string]>)
            : undefined),
          ...(!!init?.headers?.entries && typeof init?.headers?.entries === 'object'
            ? init.headers
            : undefined),
          'Content-Type': 'application/json',
          host: new URL(url).hostname,
        },
      });

    const SESSION_NAME = import.meta.env.VITE_PUBLIC_ENV__SESSION_NAME || 'sid';

    try {
      if (cookies?.[SESSION_NAME] || authTokenValue) {
        if (IS_SERVER) {
          const headers: Record<string, string> = {
            ...(('headers' in pageContext && pageContext.headers) || undefined),
            'Content-Type': 'application/json',
          };

          delete headers.host;
          delete headers.Host;
          delete headers.HOST;

          const userDataRequest = request(
            {
              input: '/api/v1/user',
              init:
                typeof AbortSignal === 'undefined'
                  ? { headers }
                  : {
                      headers,
                      // time out this request in 750ms. that's assuming:
                      // 150ms RT for first failed request, 150ms for refresh token, 150ms for re-request, + latency
                      signal: AbortSignal.timeout(
                        (import.meta.env.PROD ? 950 : 2500) * (IS_SERVER ? 1 : 10),
                      ), // dev-to-prod can take longer
                    },
            } as RequestSchema<API.WithData<API.User | null> | { expired: true }>,
            {
              withResponse: true,
              fetch: ('fetch' in pageContext && pageContext.fetch) || devFetch,
              // authToken: '',
              cache: {
                // this is short enough to prevent back-to-back requests on client
                maxAge: 10 * 1000,
                on: 'client',
                // ensure the cache key for this uses the cookie SID
                withCredentials: IS_SERVER ? true : false,
              },
            },
          );

          if (IS_SERVER) {
            const userData = await userDataRequest;

            if (!user && !isExpired(userData?.data) && userData?.data?.data) {
              user = userData.data.data;
            }
          } else {
            // Special case: page that does not need user session, but user is logged in.
            // To avoid a hydration error, we pass the promise of this request to the RasContext
            // That will await it and add it to the store asynchronously
            userPromise = userDataRequest;
          }
        }
      } else {
        user = null;
      }
    } catch (error) {
      if (error instanceof ExpiredSessionError) {
        // do nothing -- frontend will show this error
      } else if ((error as RequestError)?.code === 23) {
        // 23 = TimeoutError
        console.info('TimeoutError: /api/v1/user');
      } else {
        console.error(error);

        // @todo should this only look for 401 code?
        if (error instanceof RequestError && error.code >= 400 && error.code <= 499) {
          // User seems to be unauthorized/unauthenticated
          user = null;
        }
      }
    }
  }

  if (user === null) {
    // User is definitely logged out
    if (!guardOptions?.ignoreLoggedOut) {
      throw render('/signin', "You can't access this page until after you log in.");
    }
  }

  const result =
    user === undefined && userPromise === undefined ? undefined : { user, userPromise };

  // Check if guard needs to redirect with data

  if (guardOptions?.redirectTo) {
    const to = guardOptions.redirectTo(result);

    if (to) {
      throw redirect(...to);
    }
  }

  // Check if guard needs to render with data

  if (guardOptions?.renderTo) {
    const to = guardOptions.renderTo(result);

    if (to) {
      throw render(...to);
    }
  }

  return result;
}
